import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface HeaderBreadcrumbsProviderProps {
  children: ReactNode
}

/** Provides state for page components to render breadcrumbs in the app header. */
export function HeaderBreadcrumbsProvider({
  children,
}: HeaderBreadcrumbsProviderProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<ReactNode>(null)
  const value = useMemo(() => ({ breadcrumbs, setBreadcrumbs }), [breadcrumbs])
  return (
    <HeaderBreadcrumbsContext.Provider value={value}>
      {children}
    </HeaderBreadcrumbsContext.Provider>
  )
}

/** Returns the current breadcrumb content set by a page component. */
export function useHeaderBreadcrumbs(): ReactNode {
  return useContext(HeaderBreadcrumbsContext).breadcrumbs
}

/**
 * Sets breadcrumb content in the header and clears it on unmount.
 * The `content` argument should be memoized (e.g. via `useMemo`) to
 * avoid unnecessary re-renders.
 */
export function useSetHeaderBreadcrumbs(content: ReactNode) {
  const { setBreadcrumbs } = useContext(HeaderBreadcrumbsContext)
  const stableSetter = useCallback(
    (value: ReactNode) => setBreadcrumbs(value),
    [setBreadcrumbs]
  )

  useEffect(() => {
    stableSetter(content)
    return () => stableSetter(null)
  }, [content, stableSetter])
}

interface HeaderBreadcrumbsContextValue {
  breadcrumbs: ReactNode
  setBreadcrumbs: (content: ReactNode) => void
}

const HeaderBreadcrumbsContext = createContext<HeaderBreadcrumbsContextValue>({
  breadcrumbs: null,
  setBreadcrumbs: () => {},
})
